import React, { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { base_URL } from "../../../config/config";
import { useQuery } from "@tanstack/react-query";

export default function StatusDropdown({ enquiry, updateStatus, user }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { data: messagesData, isFetching, refetch } = useQuery({
    queryKey: ["messages", enquiry?._id],
    queryFn: async () => {
      const response = await axios.get(`${base_URL}/api/message/get/${enquiry._id}`);
      return response.data;
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
  });

  const statusOptions = ["Proposal Sent", "Connected", "Booking Completed", "Lost"];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const hasVendorMessage = (data) => {
    if (!data) return false;
    const ourSenders = ["vendor", "admin"];
    const messages = data?.messages ?? [];
    for (const item of messages) {
      if (Array.isArray(item?.message)) {
        if (item.message.some((msg) => ourSenders.includes(msg?.sender?.toLowerCase()))) return true;
      }
      if (ourSenders.includes(item?.sender?.toLowerCase())) return true;
    }
    return false;
  };

  const checkAndOpen = (data) => {
    if (hasVendorMessage(data)) {
      setIsOpen(true);
    } else {
      toast("Connect with customer first to change the status", { icon: "💬" });
    }
  };

  const handleClick = async () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    // Use cached data on subsequent clicks — no API call
    if (messagesData !== undefined) {
      checkAndOpen(messagesData);
      return;
    }

    // First click — always call checkAndOpen so API errors / empty responses
    // are treated as "no vendor message" instead of silently doing nothing
    const result = await refetch();
    checkAndOpen(result.data ?? null);
  };

  const handleStatusChange = (newStatus) => {
    updateStatus(enquiry?._id, newStatus, `${newStatus} by ${user?.email}`);
    if (enquiry) {
      enquiry.status = newStatus;
    }
    setIsOpen(false);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "New":
      case "Not Accepted":
        return "bg-[#E4EFFF] text-[#1252B1]";
      case "Active":
      case "Connected":
        return "bg-[#EAF9F7] text-[#039F8D]";
      case "Proposal Sent":
        return "bg-[#FFF4E5] text-[#E07B00]";
      case "Successful":
      case "Booking Completed":
        return "bg-[#DFDFFF] text-[#4A49D9]";
      case "Lost":
        return "bg-[#EBEBEB] text-[#717171]";
      case "Venue Declined":
        return "bg-[#FF3939]/20 text-[#FF3939]";
      case "Customer Declined":
        return "bg-[#FFE8EE] text-[#D03660]";
      default:
        return "bg-[#E4EFFF] text-[#1252B1]";
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`px-3 flex items-center justify-center gap-x-2 py-2 rounded-lg text-[14px] ${isFetching ? "cursor-wait opacity-70" : "cursor-pointer"} ${getStatusStyles(enquiry?.status)}`}
        onClick={handleClick}
      >
        {enquiry?.status === "Venue Declined"
          ? "venue declined"
          : enquiry?.status === "Not Accepted"
            ? "New"
            : enquiry?.status === "Customer Declined"
              ? "client declined"
              : enquiry?.status === "Active" || enquiry?.status === "New"
                ? "Connected"
                : enquiry?.status === "Successful"
                  ? "Booking Completed"
                  : enquiry?.status}
        <span className="flex items-center justify-center">
          <img src="/downarrow.svg" alt="Arrow" />
        </span>
      </div>

      {isOpen && (
        <div className="absolute right-12 -top-16 bg-white shadow-lg rounded-lg z-10">
          {statusOptions?.map((option) => (
            <div
              key={option}
              onClick={() => handleStatusChange(option)}
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${option === enquiry?.status
                ? "bg-gray-200 text-blue-600"
                : "text-gray-800"
                }`}
            >
              {option === "Active" ? "Ongoing" : option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
